import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, YAxis, ResponsiveContainer, Tooltip } from 'recharts';
import SearchableSelect from '../SearchableSelect';
import FlagIcon from '../FlagIcon';
import SparkleChartLoading from './SparkleChartLoading';
import { getAvailableCurrencies } from '../../utils/currencyUtils';
import * as exchangeRateActions from '../../store/actions/exchangeRate';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import {
  DEFAULT_DECIMAL_LAYOUT,
  formatAmountInputValue,
  formatNumberWithDecimalLayout,
  parseAmountInputValue,
} from '../../utils/userPreferencesHelpers';

const SEND_CURRENCY = 'EUR';
const DEFAULT_SEND_AMOUNT = 1000;

const TIMEFRAME_OPTIONS = [
  { value: '1w', label: '1W' },
  { value: '1m', label: '1M' },
  { value: '1y', label: '1Y' },
];

function getDateRange(timeframeValue) {
  const endDate = new Date();
  const startDate = new Date();
  switch (timeframeValue) {
    case '1w':
      startDate.setDate(endDate.getDate() - 7);
      break;
    case '1m':
      startDate.setMonth(endDate.getMonth() - 1);
      break;
    case '1y':
      startDate.setFullYear(endDate.getFullYear() - 1);
      break;
    default:
      startDate.setDate(endDate.getDate() - 7);
  }
  return {
    startDate: startDate.toISOString().split('T')[0],
    endDate: endDate.toISOString().split('T')[0],
  };
}

const inputClassName =
  'w-full rounded-[12px] border-[1.5px] border-[#e5e7eb] bg-[#fafbfc] py-[18px] pl-5 pr-[130px] text-[24px] font-medium tabular-nums text-[var(--n900)] outline-none font-[family-name:var(--font-ui)] transition-[border-color,background] duration-150 focus:border-[var(--n800)]';

function TransferCalculator() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currencies: configCurrencies, countries: configCountries } = useSelector((state) => state.config);
  const { latestRate, historicalRates, gettingLatestRate, gettingHistoricalRates, latestRateError, historicalRatesError } = useSelector(
    (state) => state.exchangeRate,
  );
  const { preferences, formatNumber, decimalLayout } = useNumberFormat();

  const [sendAmount, setSendAmount] = useState(() =>
    formatNumberWithDecimalLayout(DEFAULT_SEND_AMOUNT, DEFAULT_DECIMAL_LAYOUT),
  );
  const [toCurrency, setToCurrency] = useState('');
  const [timeframe, setTimeframe] = useState('1m');
  const previousDecimalLayoutRef = useRef(DEFAULT_DECIMAL_LAYOUT);

  useEffect(() => {
    if (previousDecimalLayoutRef.current === decimalLayout) return;
    const previousLayout = previousDecimalLayoutRef.current;
    previousDecimalLayoutRef.current = decimalLayout;
    setSendAmount((prev) => {
      const amount = parseAmountInputValue(prev, previousLayout) || DEFAULT_SEND_AMOUNT;
      return formatNumberWithDecimalLayout(amount, decimalLayout);
    });
  }, [decimalLayout]);

  const currencies = useMemo(() => {
    if (!configCurrencies?.length) return [];
    const list = getAvailableCurrencies({ currencies: configCurrencies, countries: configCountries });
    return list.map((c) => ({ ...c, value: c.code, label: `${c.code} – ${c.name || c.code}` }));
  }, [configCurrencies, configCountries]);

  const currencyOptions = useMemo(
    () =>
      currencies
        .filter((c) => c.code !== SEND_CURRENCY)
        .map((c) => ({
          value: c.code,
          label: `${c.code} – ${c.name || c.code}`,
          displayLabel: `${c.code} – ${c.name || c.code}`,
          symbol: c.symbol || '',
          searchText: [c.code, c.name, c.flag].filter(Boolean).join(' '),
        })),
    [currencies],
  );

  const areCurrenciesSame = SEND_CURRENCY === toCurrency;

  useEffect(() => {
    if (currencies.length > 0 && !toCurrency) {
      const first = currencies.find((c) => c.code !== SEND_CURRENCY) || currencies[0];
      setToCurrency(first.code);
    }
  }, [currencies, toCurrency]);

  useEffect(() => {
    if (!toCurrency || areCurrenciesSame) return;
    dispatch(exchangeRateActions.getLatestRate(SEND_CURRENCY, toCurrency));
    const { startDate, endDate } = getDateRange(timeframe);
    dispatch(exchangeRateActions.getHistoricalRates(SEND_CURRENCY, toCurrency, startDate, endDate));
  }, [toCurrency, timeframe, areCurrenciesSame, dispatch]);

  const handleToCurrencyChange = (e) => setToCurrency(e.target.value);

  const handleAmountChange = (e) => {
    setSendAmount(formatAmountInputValue(e.target.value, preferences));
  };

  const sendNum = parseAmountInputValue(sendAmount, preferences);
  const rate = latestRate?.rate || (areCurrenciesSame ? 1 : 0);
  const receiveNum = areCurrenciesSame ? sendNum : sendNum * rate;
  const receiveAmount = receiveNum > 0 ? formatNumber(receiveNum) : '';

  const chartData = useMemo(() => {
    if (!historicalRates?.rates) return [];
    return Object.entries(historicalRates.rates)
      .sort(([a], [b]) => new Date(a) - new Date(b))
      .map(([date, value]) => ({
        date,
        rate: value,
        displayDate: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      }));
  }, [historicalRates]);

  const isLoadingChart = gettingHistoricalRates;
  const showChartData = chartData.length > 0 && !historicalRatesError;

  const handleSendMoney = () => {
    const params = new URLSearchParams();
    if (toCurrency) params.set('currency', toCurrency);
    if (sendNum > 0) {
      params.set('sendAmount', sendAmount.trim());
      params.set('quoteBy', 'send');
    }
    const query = params.toString();
    navigate(query ? `/send-money?${query}` : '/send-money');
  };

  let rateDisplay = '—';
  if (gettingLatestRate) {
    rateDisplay = '…';
  } else if (rate) {
    rateDisplay = formatNumber(rate, { minimumFractionDigits: 4, maximumFractionDigits: 4 });
  }

  return (
    <section data-tour='transfer-calculator'>
      <p className='section-label mb-3.5'>Calculator</p>

      <div className='overflow-visible rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)]'>
        <div className='flex flex-wrap items-baseline justify-between gap-4 border-b border-[var(--color-border)] px-6 py-[18px]'>
          <div>
            <div className='mb-1 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>FX rate</div>
            <div className='font-display text-[28px] font-normal tracking-[-0.015em] text-[var(--n900)]'>
              1 {SEND_CURRENCY} ={' '}
              <em className='font-normal italic text-[var(--n500)]'>{rateDisplay}</em>{' '}
              {toCurrency || '—'}
            </div>
          </div>
          <span className='inline-flex shrink-0 items-center gap-[7px] text-[11.5px] font-medium text-[var(--text-secondary)]'>
            <span className='h-[7px] w-[7px] shrink-0 rounded-full bg-[rgb(34,197,94)]' aria-hidden='true' />
            Live FX rate
          </span>
        </div>

        <div className='grid lg:grid-cols-2'>
          <div className='border-b border-[var(--color-border)] p-6 lg:border-b-0 lg:border-r'>
            <label htmlFor='calculator-send-amount' className='mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>
              You send
            </label>
            <div className='relative'>
              <input id='calculator-send-amount' type='text' inputMode='decimal' value={sendAmount} onChange={handleAmountChange} className={inputClassName} />
              <div className='pointer-events-none absolute right-4 top-1/2 flex -translate-y-1/2 items-center gap-2 text-sm font-medium tracking-[0.03em] text-[var(--text-secondary)]'>
                <FlagIcon currencyCode={SEND_CURRENCY} className='h-4 w-4 shrink-0 rounded-sm' />
                <span>{SEND_CURRENCY}</span>
              </div>
            </div>

            <div className='h-4' />

            <label htmlFor='calculator-receive-amount' className='mb-2 block text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>
              Recipient gets
            </label>
            <div className='relative z-10'>
              <input id='calculator-receive-amount' type='text' readOnly value={receiveAmount} className={inputClassName} />
              <div className='absolute right-1.5 top-1/2 w-[130px] -translate-y-1/2'>
                <SearchableSelect
                  id='to-currency'
                  name='to'
                  value={toCurrency}
                  onChange={handleToCurrencyChange}
                  options={currencyOptions}
                  className='w-full'
                  placeholder='Currency'
                  menuMinWidth='w-[220px]'
                  menuAlign='right'
                  searchPlaceholder='Search currency or country'
                  menuMaxHeight={360}
                  renderSelected={(code, opt) =>
                    opt ? (
                      <span className='flex items-center gap-2 text-sm font-medium text-[var(--n800)]'>
                        <FlagIcon currencyCode={code} className='h-4 w-4 rounded-sm shrink-0' />
                        <span>{code}</span>
                      </span>
                    ) : null
                  }
                  renderOption={(opt) => (
                    <>
                      <FlagIcon currencyCode={opt.value} className='h-4 w-4 shrink-0 rounded-sm' />
                      <span className='font-medium'>{opt.value}</span>
                      {opt.symbol ? (
                        <span className='ml-auto text-[12px] font-normal text-[var(--text-tertiary)]'>{opt.symbol}</span>
                      ) : null}
                    </>
                  )}
                />
              </div>
            </div>

            <button
              type='button'
              onClick={handleSendMoney}
              disabled={!sendNum || gettingLatestRate}
              className={`mt-4 flex w-full items-center justify-center gap-2 rounded-lg px-[18px] py-3 text-[13.5px] font-medium transition-[background,color] duration-150 font-[family-name:var(--font-ui)] ${
                !sendNum || gettingLatestRate
                  ? 'cursor-not-allowed border border-[var(--color-border)] text-[var(--text-tertiary)]'
                  : 'border border-[var(--n800)] text-[var(--n800)] hover:bg-[var(--off)]'
              }`}
            >
              Send
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M5 12h14M12 5l7 7-7 7' />
              </svg>
            </button>
          </div>

          <div className='flex flex-col p-6'>
            <div className='mb-4 flex items-start justify-between gap-3'>
              <div>
                <div className='mb-0.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>
                  Rate trend
                </div>
                <div className='text-[13px] text-[var(--text-secondary)]'>
                  {SEND_CURRENCY} / {toCurrency || '—'}
                </div>
              </div>
              <div className='flex gap-0.5 rounded-[8px] bg-[var(--off)] p-[3px]'>
                {TIMEFRAME_OPTIONS.map(({ value, label }) => (
                  <button
                    key={value}
                    type='button'
                    onClick={() => setTimeframe(value)}
                    disabled={isLoadingChart}
                    className={`rounded-[6px] px-[11px] py-[5px] text-[11px] font-medium transition-colors font-[family-name:var(--font-ui)] ${
                      timeframe === value
                        ? 'bg-[var(--white)] text-[var(--n800)] shadow-[0_1px_3px_rgba(11,25,41,0.1)]'
                        : 'bg-transparent text-[var(--text-tertiary)]'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div className='min-h-[200px] flex-1'>
              {isLoadingChart && <SparkleChartLoading />}
              {!isLoadingChart && historicalRatesError && (
                <div className='flex h-full items-center justify-center text-sm text-[var(--text-tertiary)]'>Chart unavailable</div>
              )}
              {!isLoadingChart && showChartData && (
                <ResponsiveContainer width='100%' height={200}>
                  <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <YAxis
                      domain={['auto', 'auto']}
                      axisLine={false}
                      tickLine={false}
                      tick={{ fill: 'var(--text-tertiary)', fontSize: 10 }}
                      tickFormatter={(value) =>
                        formatNumber(value, { minimumFractionDigits: 0, maximumFractionDigits: 4 })
                      }
                      width={56}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--white)',
                        border: '1px solid var(--color-border)',
                        borderRadius: 8,
                        fontSize: 11,
                        fontFamily: 'var(--font-ui)',
                      }}
                      labelStyle={{ display: 'none' }}
                      formatter={(value) => [
                        formatNumber(value, { minimumFractionDigits: 4, maximumFractionDigits: 4 }),
                        '',
                      ]}
                    />
                    <Line type='monotone' dataKey='rate' stroke='var(--n500)' strokeWidth={2} dot={false} activeDot={{ r: 4, fill: 'var(--n500)' }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
              {!isLoadingChart && !historicalRatesError && !showChartData && toCurrency && !areCurrenciesSame && <SparkleChartLoading />}
            </div>
          </div>
        </div>

        {latestRateError && <p className='border-t border-[var(--color-border)] px-6 py-3 text-xs text-[var(--color-error)]'>Rate unavailable. Try another pair.</p>}
      </div>
    </section>
  );
}

export default TransferCalculator;
